import useSWR, { SWRResponse } from "swr";

import { useAuthErrorHandler } from "../common/hooks";
import { ResponseWithPagination } from "../types/common";
import { UserRole } from "../types/userCurrent";
import { User } from "../types/users";

import request, { ApiError } from "./common";
import { UserResponsePayload } from "./project";

export type UserCreateReq = {
    email: string;
    firstName: string;
    lastName: string;
    role: UserRole;
    userProfileId: number;
};

export type UserUpdateReq = {
    firstName: string;
    lastName: string;
    role: string;
    userProfileId: number;
};

export type UserRequestBody = {
    emails?: string[];
    userProfileType?: string;
    userRole?: string;
    userProfileName?: string;
};

const path = "/users";

const usersApi = {
    getUsers: (
        emails?: string[],
        userProfileType?: string,
        userRole?: string,
        userProfileName?: string,
    ): Promise<User[]> => {
        const filter: UserRequestBody = {};

        if (emails && emails.length) filter.emails = emails;

        if (userProfileType) filter.userProfileType = userProfileType;

        if (userRole) filter.userRole = userRole;

        if (userProfileName) filter.userProfileName = userProfileName;

        const isFilterMode = Object.keys(filter).length > 0;
        const apiPath = isFilterMode ? `${path}/filter` : path;
        const method = isFilterMode ? "POST" : "GET";

        return request<User[], UserRequestBody>(apiPath, method, filter);
    },
    getUsersPaging: (
        emails?: string[],
        userProfileType?: string,
        userRole?: string,
        userProfileName?: string,
        page?: number,
        size?: number,
    ): Promise<ResponseWithPagination<User[]>> => {
        const filter: UserRequestBody = {};

        if (emails && emails.length) filter.emails = emails;

        if (userProfileType) filter.userProfileType = userProfileType;

        if (userRole) filter.userRole = userRole;

        if (userProfileName) filter.userProfileName = userProfileName;

        const isFilterMode = Object.keys(filter).length > 0;
        const apiPath = isFilterMode ? `${path}/filter/paging?page=${page}&size=${size}` : `${path}/paging?page=${page}&size=${size}`;
        const method = isFilterMode ? "POST" : "GET";

        return request<ResponseWithPagination<User[]>, UserRequestBody>(apiPath, method, filter);
    },
    activate: (userId: string): Promise<User> => {
        return request<User, null>(`${path}/id/${userId}/activate`, "PATCH");
    },
    deactivate: (userId: string): Promise<User> => {
        return request<User, null>(`${path}/id/${userId}/deactivate`, "PATCH");
    },
    create: (payload: UserCreateReq): Promise<UserResponsePayload> => {
        return request<UserResponsePayload, UserCreateReq>(
            `${path}`,
            "POST",
            payload,
        );
    },
    update: (payload: UserUpdateReq, userId: string): Promise<null> => {
        return request<null, UserUpdateReq>(
            `${path}/id/${userId}`,
            "PUT",
            payload,
        );
    },
    disableHsbcOperatorEmailNoti: (userId: string) => {
        return request<null, null>(
            `${path}/id/${userId}/disable-hsbc-operator-email-notif`,
            "PATCH",
        );
    },
    enableHsbcOperatorEmailNoti: (userId: string) => {
        return request<null, null>(
            `${path}/id/${userId}/enable-hsbc-operator-email-notif`,
            "PATCH",
        );
    },
    resendActivation: (userId: string) => {
        return request<null, null>(
            `${path}/id/${userId}/resend-activation`,
            "PATCH",
        );
    },
    review: (id: string, approvalStatus: number, rejectReason: string | null): Promise<User> => {
        const payload: ReviewApprovePayload = {
            id: id,
            approvalStatus: approvalStatus,
            rejectReason: rejectReason ? rejectReason : "",
        }
        return request<User, ReviewApprovePayload>(
            `${path}/approve`,
            "POST",
            payload,
        );
    },
};

export const useUserList = (
    emails?: string[],
    userProfileId?: number,
    userRole?: string,
): SWRResponse<User[], ApiError> => {
    const params = [];
    const onError = useAuthErrorHandler();

    if (emails && emails.length) params.push(`emails=${emails}`);
    if (typeof userProfileId == "number")
        params.push(`userProfileId=${userProfileId}`);
    if (userRole) params.push(`userRole=${userRole}`);

    return useSWR<User[], ApiError>(`${path}?${params.join("&")}`, request, {
        onError,
    });
};

export const useUserListPaging = (
    emails?: string[],
    userProfileId?: number,
    userRole?: string,
    page?: number,
    size?: number,
): SWRResponse<ResponseWithPagination<User[]>, ApiError> => {
    const params = [];
    const onError = useAuthErrorHandler();

    if (emails && emails.length) params.push(`emails=${emails}`);
    if (typeof userProfileId == "number")
        params.push(`userProfileId=${userProfileId}`);
    if (userRole) params.push(`userRole=${userRole}`);

    return useSWR<ResponseWithPagination<User[]>, ApiError>(`${path}?${params.join("&")}`, request, {
        onError,
    });
};

export const useUserById = (
    userId: string,
): SWRResponse<useUserByIdResponse, ApiError> => {
    const onError = useAuthErrorHandler();
    return useSWR<useUserByIdResponse, ApiError>(
        userId ? `${path}/id/${userId}` : "",
        request,
        { onError },
    );
};

export type useUserByIdResponse = User & {
    isEnableInvEmailNotif: boolean;
    isEnableProfileManagement: boolean;
    isEnableUserManagement: boolean;
};

type ReviewApprovePayload = {
    id: string;
    approvalStatus: number;
    rejectReason: string;
};

export default usersApi;
